import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import appRoutes from 'app.routes';
import { AUTH_LOGIN_KEY, AUTH_REFRESHING } from 'api/http';

export const useRefreshToken = (refreshInterval = 30 * 60 * 1000) => {
  const location = useLocation();
  const lastRefreshTime = useRef<number>(Number(localStorage.getItem('LAST_TOKEN_REFRESH')) || 0);
  const timerRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const isRefreshingRef = useRef<boolean>(false);

  const refreshToken = useCallback(() => {
    // Take authUrl from localStorage
    const authUrl = typeof localStorage !== 'undefined' ? localStorage.getItem(AUTH_LOGIN_KEY) : undefined;

    // If authUrl is null or undefined then do nothing
    if (!authUrl || location.pathname.includes(appRoutes.authRedirect())) {
      return;
    }

    // Prevent multiple refreshes
    if (isRefreshingRef.current) {
      return;
    }

    isRefreshingRef.current = true;

    // Set flag for auth redirect to know this is a refresh operation
    localStorage.setItem(AUTH_REFRESHING, 'true');

    // Update refresh token time
    const now = Date.now();
    lastRefreshTime.current = now;
    localStorage.setItem('LAST_TOKEN_REFRESH', now.toString());

    // Clean up any existing iframe
    if (iframeRef.current && iframeRef.current.parentNode) {
      try {
        document.body.removeChild(iframeRef.current);
        iframeRef.current = null;
      } catch (e) {
        console.error('Error removing existing iframe:', e);
      }
    }

    // Create new iframe for silent token refresh
    try {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = authUrl;
      document.body.appendChild(iframe);
      iframeRef.current = iframe;

      // Remove iframe after a timeout
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }

      timerRef.current = window.setTimeout(() => {
        if (iframe && iframe.parentNode) {
          try {
            document.body.removeChild(iframe);
          } catch (e) {
            console.error('Error removing iframe in timeout:', e);
          } finally {
            iframeRef.current = null;
            isRefreshingRef.current = false;
          }
        }
      }, 10000); // Increase timeout to 10 seconds for slow connections
    } catch (e) {
      console.error('Error during token refresh:', e);
      isRefreshingRef.current = false;
    }
  }, [location.pathname]);

  // Check if token needs refresh (run once at component mount and path change)
  useEffect(() => {
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTime.current;

    // Need refresh if last refresh was more than refreshInterval ago
    if (timeSinceLastRefresh >= refreshInterval) {
      refreshToken();
    }

    // Set up recurring check for token refresh
    intervalRef.current = window.setInterval(
      () => {
        const currentTime = Date.now();
        const elapsed = currentTime - lastRefreshTime.current;

        if (elapsed >= refreshInterval && !isRefreshingRef.current) {
          refreshToken();
        }
      },
      Math.min(refreshInterval / 4, 5 * 60 * 1000)
    ); // Check every 1/4 of refresh interval or 5 minutes, whichever is smaller

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      if (iframeRef.current && iframeRef.current.parentNode) {
        try {
          document.body.removeChild(iframeRef.current);
          iframeRef.current = null;
        } catch (e) {
          console.error('Error removing iframe in cleanup:', e);
        }
      }

      isRefreshingRef.current = false;
    };
  }, [refreshToken, refreshInterval]);

  return null;
};
